import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Menu,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/es";
import { FC, useEffect, useState, MouseEvent } from "react";
import { BarFilters, MonthYear } from "../../../../interfaces/SuperAdmin/Pages/iDataCreditos";
import FilterListIcon from "@mui/icons-material/FilterList";
dayjs.locale("es");

interface Props {
  months: number;
  startMonth: MonthYear | undefined;
  creditsFilters: BarFilters;
  changeMonths: (value: number) => void;
  updateStartingMonth: (month: number, year: number) => void;
  handleToggleCredit: (value: string) => void;
}

const FilterParams: FC<Props> = ({
  months,
  startMonth,
  creditsFilters,
  changeMonths,
  updateStartingMonth,
  handleToggleCredit
}) => {
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchorEl);

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Con esta función, asignamos al DatePicker la fecha por defecto que viene del backend
  useEffect(() => {
    if (startMonth?.month && startMonth?.year) {
      const newDate = dayjs()
        .year(startMonth.year)
        .month(startMonth.month - 1);

      setSelectedDate(newDate);
    }
  }, [startMonth?.month, startMonth?.year]);

  return (
    <Box sx={{ display: "flex", alignItems: "end", gap: 4 }}>
      {/* Tipos de créditos */}
      <Button
        onClick={handleOpen}
        sx={{
          backgroundColor: "#336B87",
          color: "#fff",
          borderRadius: "999px",
          textTransform: "none",
          px: 2,
          py: 1,
          "&:hover": {
            backgroundColor: "#2b5a73",
          },
        }}
        endIcon={<FilterListIcon />}
      >
        Filtros
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 1.25 }}
      >
        <MenuItem disableRipple>
          <Box display="flex" gap={4}>
            {/* Grupo de Filtro 1 */}
            <Box>
              <Typography fontWeight="bold" fontSize={18} mb={1}>
                Tipos de créditos
              </Typography>
              <Box display="flex" flexDirection={"column"}>
                {creditsFilters.credits.map((credit) => (
                  <FormControlLabel
                    key={credit.value}
                    control={
                      <Checkbox
                        checked={credit.isActive}
                        onChange={() => handleToggleCredit(credit.value)}
                        sx={{
                          color: "#336B87",
                          "&.Mui-checked": {
                            color: "#336B87",
                          },
                          borderRadius: "50%",
                        }}
                      />
                    }
                    label={credit.label}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </MenuItem>
      </Menu>

      {/* Month Selector */}
      <Box>
        <Typography component="span" fontWeight="bold">
          Mes:
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DemoContainer
            components={["DatePicker", "DatePicker", "DatePicker"]}
          >
            <DatePicker
              views={["month", "year"]}
              value={selectedDate}
              onChange={(newValue: Dayjs | null) => {
                setSelectedDate(newValue);
                if (newValue) {
                  updateStartingMonth(newValue.month() + 1, newValue.year());
                }
              }}
              slotProps={{
                textField: {
                  sx: {
                    height: 40,
                    fontSize: 16,
                    "& .MuiInputBase-root": {
                      height: 40,
                      fontSize: 16,
                    },
                    "& .MuiInputBase-input": {
                      fontSize: 16,
                      padding: "8px 12px",
                    },
                    "& .MuiSvgIcon-root": {
                      fontSize: "1rem", // tamaño del ícono del calendario
                    },
                  },
                },
              }}
            />
          </DemoContainer>
        </LocalizationProvider>
      </Box>

      {/* Sekector de N° de meses */}
      <Box>
        <Typography component="span" fontWeight="bold">
          N° de meses:
        </Typography>
        <Select
          value={months}
          onChange={(e) => changeMonths(Number(e.target.value))}
          fullWidth
          size="small"
          sx={{ mt: 1 }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 200,
                overflowY: "auto",
              },
            },
          }}
        >
          {Array.from({ length: 21 }, (_, i) => i + 4).map((num) => (
            <MenuItem key={num} value={num}>
              {num}
            </MenuItem>
          ))}
        </Select>
      </Box>
    </Box>
  );
};

export default FilterParams;
